import { Link } from 'react-router-dom'
import { motion, type Variants } from 'framer-motion'
import {
  ArrowRight,
  CheckCircle,
  ChevronRight,
  Database,
  FileText,
  GitMerge,
  Lock,
  Search,
  Shield,
  Zap,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { SEOHead } from '@/components/SEOHead'
import { ProductPageNav, ProductPageFooter } from '@/components/ProductPageNav'

// ── Animation variants ────────────────────────────────────────────────────────

const fadeUp: Variants = {
  hidden: { opacity: 0, y: 32 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.1, duration: 0.5 },
  }),
}

// ── SVG: Identity Cluster diagram ─────────────────────────────────────────────

function IdentityClusterSVG() {
  const seeds = [
    { label: 'jane@corp.io', type: 'email', color: '#3b82f6', y: 28 },
    { label: '+91 98400 12345', type: 'phone', color: '#22c55e', y: 80 },
    { label: 'Jane Kapoor', type: 'name', color: '#f59e0b', y: 132 },
    { label: 'ABCDE1234F', type: 'PAN', color: '#8b5cf6', y: 184 },
  ]

  const discovered = [
    { label: 'jane_k92', type: 'username', color: '#94a3b8', y: 40 },
    { label: 'jk@gmail.com', type: 'alias email', color: '#94a3b8', y: 90 },
    { label: 'janeK_sec', type: 'username', color: '#94a3b8', y: 140 },
    { label: '+91 77040 88221', type: 'linked phone', color: '#94a3b8', y: 190 },
  ]

  return (
    <svg
      viewBox="0 0 540 220"
      className="w-full max-w-2xl mx-auto"
      aria-label="Identity Cluster diagram"
    >
      {/* Seed chips */}
      {seeds.map((s) => (
        <g key={s.label}>
          {/* chip rect */}
          <rect x={4} y={s.y - 16} width={148} height={32} rx={6} fill="#0d1117" stroke={s.color} strokeWidth="1.4" />
          {/* colored dot */}
          <circle cx={20} cy={s.y} r={5} fill={s.color} />
          {/* label */}
          <text x={32} y={s.y - 4} fill="#e2e8f0" fontSize="9.5" fontWeight="600">{s.label}</text>
          <text x={32} y={s.y + 8} fill="#64748b" fontSize="8">{s.type}</text>
          {/* dashed line to cluster */}
          <line
            x1={152} y1={s.y}
            x2={248} y2={112}
            stroke={s.color}
            strokeOpacity="0.5"
            strokeWidth="1.2"
            strokeDasharray="6 4"
          />
        </g>
      ))}

      {/* Center cluster oval */}
      <ellipse cx={270} cy={112} rx={60} ry={42} fill="#0d1a0d" stroke="#22c55e" strokeWidth="2" />
      <ellipse cx={270} cy={112} rx={52} ry={35} fill="#22c55e" fillOpacity="0.06" />
      <text x={270} y={107} textAnchor="middle" fill="#22c55e" fontSize="11" fontWeight="700">Identity</text>
      <text x={270} y={122} textAnchor="middle" fill="#22c55e" fontSize="11" fontWeight="700">Cluster</text>

      {/* Discovered variants */}
      {discovered.map((d) => (
        <g key={d.label}>
          {/* line from cluster to chip */}
          <line
            x1={330} y1={112}
            x2={388} y2={d.y}
            stroke="#1e3a1e"
            strokeWidth="1.2"
            strokeDasharray="5 3"
          />
          {/* chip rect */}
          <rect x={388} y={d.y - 16} width={146} height={32} rx={6} fill="#0d1117" stroke="#1e3a1e" strokeWidth="1.2" />
          <circle cx={403} cy={d.y} r={4} fill="#334155" />
          <text x={413} y={d.y - 4} fill="#94a3b8" fontSize="9.5">{d.label}</text>
          <text x={413} y={d.y + 8} fill="#475569" fontSize="8">{d.type}</text>
        </g>
      ))}
    </svg>
  )
}

// ── SVG: PivotChain graph ─────────────────────────────────────────────────────

function PivotChainSVG() {
  const nodes = [
    { x: 70,  y: 100, label: 'jane@corp.io', sub: 'seed email', color: '#3b82f6' },
    { x: 210, y: 52,  label: 'Breach hit',   sub: 'conf 0.97',  color: '#ef4444' },
    { x: 210, y: 148, label: 'jane_k92',     sub: 'conf 0.89',  color: '#8b5cf6' },
    { x: 350, y: 100, label: '+91 98400…',   sub: 'conf 0.84',  color: '#22c55e' },
    { x: 480, y: 52,  label: '@janeK_sec',   sub: 'conf 0.76',  color: '#f59e0b' },
    { x: 480, y: 148, label: 'jk@gmail.com', sub: 'conf 0.71',  color: '#3b82f6' },
  ]

  const edges: [number, number, string][] = [
    [0, 1, '#ef4444'],
    [0, 2, '#8b5cf6'],
    [1, 3, '#22c55e'],
    [2, 3, '#22c55e'],
    [3, 4, '#f59e0b'],
    [3, 5, '#3b82f6'],
  ]

  return (
    <svg
      viewBox="0 0 560 200"
      className="w-full max-w-xl mx-auto"
      aria-label="PivotChain breach pivot graph"
    >
      {edges.map(([a, b, color], i) => (
        <line
          key={i}
          x1={nodes[a].x} y1={nodes[a].y}
          x2={nodes[b].x} y2={nodes[b].y}
          stroke={color}
          strokeOpacity="0.3"
          strokeWidth="1.5"
          strokeDasharray="5 3"
        />
      ))}
      {nodes.map((n, i) => (
        <g key={i}>
          <circle cx={n.x} cy={n.y} r={30} fill="#0d1117" stroke={n.color} strokeWidth="1.8" />
          <circle cx={n.x} cy={n.y} r={23} fill={n.color} fillOpacity="0.08" />
          <text x={n.x} y={n.y - 3} textAnchor="middle" fill={n.color} fontSize="8" fontWeight="700">
            {n.label.length > 12 ? n.label.slice(0, 12) + '…' : n.label}
          </text>
          <text x={n.x} y={n.y + 9} textAnchor="middle" fill="#64748b" fontSize="7.5">{n.sub}</text>
        </g>
      ))}
    </svg>
  )
}

// ── SVG: Workflow pipeline ────────────────────────────────────────────────────

const dashAnimation = `
@keyframes dashMove {
  from { stroke-dashoffset: 0; }
  to   { stroke-dashoffset: -36; }
}
`

function WorkflowPipelineSVG() {
  const steps = [
    { n: '01', label: 'Seed Input',          sub: 'Email / Phone / Name / PAN' },
    { n: '02', label: 'Identity Normalise',  sub: 'Dedup & cluster' },
    { n: '03', label: 'Breach Pivot',        sub: 'PivotChain engine' },
    { n: '04', label: 'Enrichment',          sub: '6 parallel modules' },
    { n: '05', label: 'Risk Scoring',        sub: 'Confidence 0–1' },
    { n: '06', label: 'Dossier',             sub: 'PDF export' },
  ]

  const boxW = 120
  const boxH = 72
  const gap = 28
  const totalW = steps.length * boxW + (steps.length - 1) * gap
  const arrowY = 36

  return (
    <svg
      viewBox={`0 0 ${totalW + 20} ${boxH + 20}`}
      className="w-full max-w-4xl mx-auto"
      aria-label="Profiler workflow pipeline"
    >
      <style>{dashAnimation}</style>
      {steps.map((s, i) => {
        const x = 10 + i * (boxW + gap)
        const colors = ['#22c55e', '#3b82f6', '#ef4444', '#8b5cf6', '#f59e0b', '#06b6d4']
        const c = colors[i]
        return (
          <g key={s.n}>
            {/* connector arrow */}
            {i > 0 && (
              <>
                <line
                  x1={x - gap} y1={10 + arrowY}
                  x2={x}        y2={10 + arrowY}
                  stroke={c}
                  strokeOpacity="0.5"
                  strokeWidth="1.5"
                  strokeDasharray="8 4"
                  style={{ animation: 'dashMove 1.2s linear infinite' }}
                />
                <polygon
                  points={`${x - 6},${10 + arrowY - 4} ${x},${10 + arrowY} ${x - 6},${10 + arrowY + 4}`}
                  fill={c}
                  fillOpacity="0.6"
                />
              </>
            )}
            {/* box */}
            <rect x={x} y={10} width={boxW} height={boxH} rx={8} fill="#0d1117" stroke={c} strokeWidth="1.4" />
            {/* top color bar */}
            <rect x={x} y={10} width={boxW} height={3} rx={2} fill={c} />
            {/* step number */}
            <text x={x + 10} y={28} fill={c} fontSize="9" fontFamily="monospace" fontWeight="700">{s.n}</text>
            {/* label */}
            <text x={x + boxW / 2} y={46} textAnchor="middle" fill="#e2e8f0" fontSize="10" fontWeight="700">{s.label}</text>
            {/* sub-label */}
            <text x={x + boxW / 2} y={60} textAnchor="middle" fill="#64748b" fontSize="8.5">{s.sub}</text>
          </g>
        )
      })}
    </svg>
  )
}

// ── Dossier preview card ──────────────────────────────────────────────────────

function DossierPreview() {
  const chips = [
    { label: 'jane@corp.io', color: 'bg-blue-500/15 text-blue-400 border-blue-500/30' },
    { label: '+91 98400 12345', color: 'bg-green-500/15 text-green-400 border-green-500/30' },
    { label: 'jane_k92', color: 'bg-purple-500/15 text-purple-400 border-purple-500/30' },
    { label: 'ABCDE1234F', color: 'bg-amber-500/15 text-amber-400 border-amber-500/30' },
  ]

  const findings = [
    { source: 'LinkedIn Data Scrape 2023', date: 'Apr 2023', severity: 'HIGH',   sev: 'text-red-400 bg-red-500/10 border-red-500/25' },
    { source: 'Collection #3 Combo',       date: 'Jan 2022', severity: 'MEDIUM', sev: 'text-amber-400 bg-amber-500/10 border-amber-500/25' },
    { source: 'Canva Breach 2019',         date: 'May 2019', severity: 'LOW',    sev: 'text-green-400 bg-green-500/10 border-green-500/25' },
  ]

  const score = 74
  const circumference = 2 * Math.PI * 36
  const offset = circumference - (score / 100) * circumference

  return (
    <div className="bg-[#0d1117] border border-white/[0.09] rounded-2xl p-6 max-w-2xl mx-auto shadow-2xl shadow-black/60">
      {/* header row */}
      <div className="flex items-center justify-between mb-5 pb-4 border-b border-white/[0.06]">
        <div>
          <div className="text-xs text-slate-500 uppercase tracking-widest mb-1">Dossier Preview</div>
          <div className="text-lg font-bold text-white">Jane Kapoor</div>
          <div className="text-xs text-slate-500 mt-0.5">Generated 2026-06-04 · 42 findings · 7 sources</div>
        </div>
        {/* confidence ring */}
        <div className="flex flex-col items-center">
          <svg viewBox="0 0 88 88" className="w-16 h-16" aria-label={`Risk score ${score}`}>
            <circle cx="44" cy="44" r="36" fill="none" stroke="#1f2d1f" strokeWidth="7" />
            <circle
              cx="44" cy="44" r="36"
              fill="none"
              stroke="#ef4444"
              strokeWidth="7"
              strokeDasharray={`${circumference}`}
              strokeDashoffset={offset}
              strokeLinecap="round"
              transform="rotate(-90 44 44)"
            />
            <text x="44" y="40" textAnchor="middle" fill="#ef4444" fontSize="18" fontWeight="800">{score}</text>
            <text x="44" y="54" textAnchor="middle" fill="#64748b" fontSize="8">HIGH RISK</text>
          </svg>
        </div>
      </div>

      {/* identifier chips */}
      <div className="flex flex-wrap gap-2 mb-5">
        {chips.map((ch) => (
          <span
            key={ch.label}
            className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-mono border ${ch.color}`}
          >
            {ch.label}
          </span>
        ))}
      </div>

      {/* findings table */}
      <div className="rounded-lg overflow-hidden border border-white/[0.06]">
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-white/[0.03] border-b border-white/[0.06]">
              <th className="text-left px-4 py-2.5 text-slate-500 font-medium">Breach Source</th>
              <th className="text-left px-4 py-2.5 text-slate-500 font-medium">Date</th>
              <th className="text-left px-4 py-2.5 text-slate-500 font-medium">Severity</th>
            </tr>
          </thead>
          <tbody>
            {findings.map((f, i) => (
              <tr
                key={f.source}
                className={`border-b border-white/[0.04] last:border-0 ${i % 2 === 1 ? 'bg-white/[0.015]' : ''}`}
              >
                <td className="px-4 py-2.5 text-slate-300">{f.source}</td>
                <td className="px-4 py-2.5 text-slate-500">{f.date}</td>
                <td className="px-4 py-2.5">
                  <span className={`inline-flex px-1.5 py-0.5 rounded text-[10px] font-bold border ${f.sev}`}>
                    {f.severity}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* footer hint */}
      <div className="mt-4 flex items-center gap-2 text-[11px] text-slate-600">
        <FileText className="w-3.5 h-3.5" />
        <span>Export as PDF — analyst notes, citations, and evidence chain included</span>
      </div>
    </div>
  )
}

// ── Enrichment layer cards ────────────────────────────────────────────────────

const enrichmentCards = [
  {
    title: 'Breach Intel',
    color: 'text-green-400',
    borderColor: 'border-green-500/25',
    icon: Database,
    items: [
      '12B+ leaked record corpus',
      'Credential cluster pivoting',
      'Dark-web identity exposure',
      'Confidence-scored each hop',
    ],
  },
  {
    title: 'Corporate Registry',
    color: 'text-blue-400',
    borderColor: 'border-blue-500/25',
    icon: Shield,
    items: [
      'GST number lookup & verify',
      'MCA / CIN director info',
      'Company filings & status',
      'Beneficial ownership chain',
    ],
  },
  {
    title: 'Social Accounts',
    color: 'text-purple-400',
    borderColor: 'border-purple-500/25',
    icon: Search,
    items: [
      'holehe — 121 platform probes',
      'social-analyzer — 900+ platforms',
      'Telegram Phunter phone scan',
      'Username candidate extraction',
    ],
  },
  {
    title: 'Verification',
    color: 'text-amber-400',
    borderColor: 'border-amber-500/25',
    icon: CheckCircle,
    items: [
      'PAN & GST identity verify',
      'NMC doctor registry lookup',
      'UPI VPA holder trace',
      'Cross-source corroboration',
    ],
  },
  {
    title: 'Adverse Media',
    color: 'text-red-400',
    borderColor: 'border-red-500/25',
    icon: Zap,
    items: [
      'NLP sentiment scoring',
      'Financial crime keywords',
      'Recency-weighted ranking',
      'Thousands of global sources',
    ],
  },
  {
    title: 'Commerce Footprint',
    color: 'text-cyan-400',
    borderColor: 'border-cyan-500/25',
    icon: GitMerge,
    items: [
      'Domain WHOIS & SSL history',
      'E-commerce seller profiles',
      'GST / UPI commerce trace',
      'Payment network linkages',
    ],
  },
]

// ── Page ─────────────────────────────────────────────────────────────────────

export default function SycekProfiler() {
  return (
    <>
      <SEOHead
        title="Sycek Profiler — Identity Intelligence & Digital Footprint Analysis | Sycek OSINT"
        description="Map any digital identity with Sycek Profiler. Breach pivoting across 12B+ records, 900+ social platform probes, corporate registry lookups, PAN/GST verification, adverse media scan, and structured dossier export."
        keywords="identity intelligence, OSINT profiler, breach lookup, social discovery, PAN verification, GST lookup, NMC verification, UPI trace, adverse media, digital footprint, dossier export, PivotChain"
        canonical="/sycek-profiler"
      />

      <div className="min-h-screen bg-[#0a0f0a] text-white overflow-x-hidden">

        {/* Background grid */}
        <div
          className="fixed inset-0 opacity-10 pointer-events-none"
          style={{
            backgroundImage: `
              linear-gradient(rgba(34,197,94,0.2) 1px, transparent 1px),
              linear-gradient(90deg, rgba(34,197,94,0.2) 1px, transparent 1px)
            `,
            backgroundSize: '60px 60px',
          }}
        />

        {/* ── Nav ─────────────────────────────────────────────────────────── */}
        <ProductPageNav activePage="Sycek Profiler" />

        {/* ── Hero ─────────────────────────────────────────────────────────── */}
        <section className="relative z-10 pt-24 pb-16 px-6">
          <div className="container mx-auto max-w-5xl text-center">
            <motion.div initial="hidden" animate="visible" variants={fadeUp}>
              <Badge className="mb-6 bg-green-500/10 text-green-400 border border-green-500/25 text-xs uppercase tracking-widest px-4 py-1.5">
                Sycek Profiler — Identity Intelligence
              </Badge>
            </motion.div>

            <motion.h1
              initial="hidden" animate="visible" custom={1} variants={fadeUp}
              className="text-5xl md:text-7xl font-bold leading-tight mb-6 tracking-tight"
            >
              <span className="text-white">Map Any</span>
              <br />
              <span className="text-green-400 drop-shadow-[0_0_40px_rgba(34,197,94,0.55)]">
                Digital Identity
              </span>
            </motion.h1>

            <motion.p
              initial="hidden" animate="visible" custom={2} variants={fadeUp}
              className="text-xl text-slate-400 max-w-3xl mx-auto leading-relaxed mb-10"
            >
              Submit a single seed — email, phone, name, PAN, username, or domain. Sycek Profiler builds a complete identity cluster by pivoting across 12B+ breach records, probing 900+ social platforms, verifying corporate registries, and scoring every discovered variant — delivering a structured, cite-ready dossier.
            </motion.p>

            <motion.div
              initial="hidden" animate="visible" custom={3} variants={fadeUp}
              className="flex flex-col sm:flex-row gap-4 justify-center"
            >
              <Link to="/register">
                <Button size="lg" className="bg-green-500 hover:bg-green-600 text-black font-semibold px-8 shadow-lg shadow-green-500/40">
                  Start Profiling
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Link to="/app/profiler">
                <Button size="lg" variant="outline" className="border-white/[0.15] text-slate-300 hover:text-white hover:bg-white/[0.05]">
                  Try Demo
                </Button>
              </Link>
            </motion.div>
          </div>
        </section>

        {/* ── Stats bar ────────────────────────────────────────────────────── */}
        <section className="relative z-10 py-10 border-y border-white/[0.06] bg-white/[0.02]">
          <div className="container mx-auto px-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
              {[
                { value: '12B+',  label: 'Breach records indexed' },
                { value: '900+',  label: 'Social platforms probed' },
                { value: '60s',   label: 'Avg enrichment time' },
                { value: '40+',   label: 'Data sources fused' },
              ].map((s) => (
                <div key={s.label}>
                  <div className="text-3xl font-bold text-green-400 mb-1">{s.value}</div>
                  <div className="text-sm text-slate-500">{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── Identity Cluster ─────────────────────────────────────────────── */}
        <section className="relative z-10 py-20 px-6">
          <div className="container mx-auto max-w-4xl">
            <motion.div
              initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
              className="text-center mb-12"
            >
              <h2 className="text-3xl font-bold text-white mb-3">Seeds In. Identity Cluster Out.</h2>
              <p className="text-slate-400 max-w-2xl mx-auto">
                Any identifier you know becomes a starting point. The system normalises, deduplicates, and fuses all discoveries into a single canonical identity cluster — with every variant confidence-scored and source-attributed.
              </p>
            </motion.div>
            <motion.div
              initial="hidden" whileInView="visible" viewport={{ once: true }} custom={1} variants={fadeUp}
              className="bg-[#0d1117] border border-white/[0.07] rounded-2xl p-8"
            >
              <IdentityClusterSVG />
            </motion.div>
          </div>
        </section>

        {/* ── PivotChain section ───────────────────────────────────────────── */}
        <section className="relative z-10 py-20 px-6 bg-white/[0.015]">
          <div className="container mx-auto max-w-5xl">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              {/* SVG side */}
              <motion.div
                initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
                className="bg-[#0d1117] border border-white/[0.07] rounded-2xl p-8"
              >
                <div className="text-xs text-slate-500 uppercase tracking-widest mb-6 text-center">
                  Sample PivotChain Hop Graph
                </div>
                <PivotChainSVG />
              </motion.div>

              {/* Text side */}
              <motion.div
                initial="hidden" whileInView="visible" viewport={{ once: true }} custom={1} variants={fadeUp}
              >
                <Badge className="mb-4 bg-red-500/10 text-red-400 border border-red-500/25 text-xs uppercase tracking-widest">
                  PivotChain Engine
                </Badge>
                <h2 className="text-3xl font-bold text-white mb-4">
                  Pivot Across 12 Billion Breach Records
                </h2>
                <p className="text-slate-400 mb-6 leading-relaxed">
                  A single email can expose an entire identity graph. PivotChain traces lateral connections across 12B+ leaked credentials — discovering aliases, phone numbers, linked accounts, and usernames the subject has never publicly disclosed.
                </p>
                <ul className="space-y-3">
                  {[
                    'Email → associated phones, usernames, names',
                    'Phone → linked emails and platform accounts',
                    'Username → cross-platform profile correlation',
                    'Each hop confidence-scored (0–1 with source)',
                    'False positives are discardable with audit trail',
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-3 text-sm text-slate-300">
                      <GitMerge className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </motion.div>
            </div>
          </div>
        </section>

        {/* ── Enrichment layers ────────────────────────────────────────────── */}
        <section className="relative z-10 py-20 px-6">
          <div className="container mx-auto max-w-5xl">
            <motion.div
              initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
              className="text-center mb-12"
            >
              <h2 className="text-3xl font-bold text-white mb-3">Six Parallel Enrichment Modules</h2>
              <p className="text-slate-400 max-w-2xl mx-auto">
                Every profile run fires all six modules simultaneously — breach intel, corporate registry, social discovery, identity verification, adverse media, and commerce footprint — then merges findings into a single deduplicated dossier.
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {enrichmentCards.map((card, i) => (
                <motion.div
                  key={card.title}
                  initial="hidden" whileInView="visible" viewport={{ once: true }} custom={i * 0.5} variants={fadeUp}
                  className={`bg-[#0d1117] border ${card.borderColor} rounded-xl p-5 hover:bg-white/[0.02] transition-colors`}
                >
                  <div className="flex items-center gap-2 mb-3">
                    <card.icon className={`w-4 h-4 ${card.color} flex-shrink-0`} />
                    <span className={`text-sm font-semibold ${card.color}`}>{card.title}</span>
                  </div>
                  <ul className="space-y-1.5">
                    {card.items.map((item) => (
                      <li key={item} className="flex items-center gap-2 text-xs text-slate-400">
                        <ChevronRight className="w-3 h-3 text-slate-600 flex-shrink-0" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* ── Workflow pipeline ─────────────────────────────────────────────── */}
        <section className="relative z-10 py-20 px-6 bg-white/[0.015]">
          <div className="container mx-auto max-w-5xl">
            <motion.div
              initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
              className="text-center mb-12"
            >
              <h2 className="text-3xl font-bold text-white mb-3">End-to-End Profiling Pipeline</h2>
              <p className="text-slate-400 max-w-2xl mx-auto">
                From raw seed to exportable dossier — automated, reproducible, and fully auditable.
              </p>
            </motion.div>

            <motion.div
              initial="hidden" whileInView="visible" viewport={{ once: true }} custom={1} variants={fadeUp}
            >
              <WorkflowPipelineSVG />
            </motion.div>

            {/* Step descriptions */}
            <motion.div
              initial="hidden" whileInView="visible" viewport={{ once: true }} custom={2} variants={fadeUp}
              className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mt-8 text-center"
            >
              {[
                { n: '01', title: 'Seed Input',      body: 'Submit any identifier. The system detects type automatically.' },
                { n: '02', title: 'Normalise',        body: 'Canonicalise, deduplicate, and cluster overlapping identifiers.' },
                { n: '03', title: 'Breach Pivot',     body: 'PivotChain mines 12B+ records for lateral identity connections.' },
                { n: '04', title: 'Enrichment',       body: 'Six modules run in parallel — social, corporate, media, and more.' },
                { n: '05', title: 'Risk Scoring',     body: 'Each variant receives a 0–1 confidence score with source attribution.' },
                { n: '06', title: 'Dossier',          body: 'Structured export: citations, analyst notes, risk verdict, PDF.' },
              ].map((s) => (
                <div key={s.n} className="bg-white/[0.03] border border-white/[0.07] rounded-xl p-3">
                  <div className="text-green-500/50 text-[10px] font-mono mb-1">{s.n}</div>
                  <div className="text-xs font-semibold text-white mb-1.5">{s.title}</div>
                  <div className="text-[10px] text-slate-500 leading-relaxed">{s.body}</div>
                </div>
              ))}
            </motion.div>
          </div>
        </section>

        {/* ── Dossier preview ───────────────────────────────────────────────── */}
        <section className="relative z-10 py-20 px-6">
          <div className="container mx-auto max-w-5xl">
            <motion.div
              initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
              className="text-center mb-12"
            >
              <Badge className="mb-4 bg-slate-500/10 text-slate-400 border border-slate-500/25 text-xs uppercase tracking-widest">
                Dossier Output
              </Badge>
              <h2 className="text-3xl font-bold text-white mb-3">
                Structured Intelligence — Ready for Action
              </h2>
              <p className="text-slate-400 max-w-2xl mx-auto">
                Every profile produces a structured dossier: risk verdict, identifier chips, breach findings table, confidence meter, analyst notes, and a one-click PDF export with full evidence citations.
              </p>
            </motion.div>

            <motion.div
              initial="hidden" whileInView="visible" viewport={{ once: true }} custom={1} variants={fadeUp}
            >
              <DossierPreview />
            </motion.div>

            {/* Dossier feature bullets below */}
            <motion.div
              initial="hidden" whileInView="visible" viewport={{ once: true }} custom={2} variants={fadeUp}
              className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-10"
            >
              {[
                {
                  title: 'Source-Cited Findings',
                  body: 'Every data point is tagged with its origin breach, platform, or registry — reviewable and discardable with an audit trail.',
                  color: 'border-blue-500/20',
                },
                {
                  title: 'Analyst Notes & Labels',
                  body: 'Add context, flag discrepancies, and annotate the dossier directly before sharing with stakeholders.',
                  color: 'border-green-500/20',
                },
                {
                  title: 'PDF Export',
                  body: 'One-click PDF with all findings, confidence scores, citations, risk verdict, and analyst notes — ready for regulators or legal teams.',
                  color: 'border-purple-500/20',
                },
              ].map((f) => (
                <div key={f.title} className={`bg-white/[0.025] border ${f.color} rounded-xl p-5`}>
                  <div className="text-sm font-semibold text-white mb-2">{f.title}</div>
                  <div className="text-xs text-slate-500 leading-relaxed">{f.body}</div>
                </div>
              ))}
            </motion.div>
          </div>
        </section>

        {/* ── CTA ───────────────────────────────────────────────────────────── */}
        <section className="relative z-10 py-24 px-6">
          <div className="container mx-auto max-w-3xl text-center">
            <motion.div
              initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
            >
              <div className="bg-gradient-to-b from-green-500/10 to-transparent border border-green-500/20 rounded-3xl p-12">
                <Zap className="w-10 h-10 text-green-400 mx-auto mb-4" />
                <h2 className="text-3xl font-bold text-white mb-4">
                  Start Your First Investigation
                </h2>
                <p className="text-slate-400 mb-8 leading-relaxed">
                  Submit any seed identifier and receive a fully enriched, risk-scored dossier in under 60 seconds. No setup required.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Link to="/register">
                    <Button size="lg" className="bg-green-500 hover:bg-green-600 text-black font-semibold px-10 shadow-lg shadow-green-500/40">
                      Create Free Account
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                  <Link to="/app/profiler">
                    <Button size="lg" variant="outline" className="border-white/[0.15] text-slate-300 hover:text-white hover:bg-white/[0.05]">
                      Open Profiler
                    </Button>
                  </Link>
                </div>
                <p className="text-xs text-slate-600 mt-6">
                  <Lock className="w-3 h-3 inline mr-1" />
                  All profile data is encrypted at rest and in transit. No raw credentials are stored.
                </p>
              </div>
            </motion.div>
          </div>
        </section>

        {/* ── Footer ────────────────────────────────────────────────────────── */}
        <ProductPageFooter />

      </div>
    </>
  )
}
